import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '../../../components/common/Typography';
import TextField from '../../../components/common/TextField';
import DatePicker from '../../../components/common/DatePicker';
import Button from '../../../components/common/Button';
import { SelectBox } from '../../../components/common/SelectBox';
import FieldTooltip from './FieldTooltip';
import AiParsedDialog from './AiParsedDialog';
import { headerList } from '../headerlist';
import { isValidate, processInputValue } from '../util';
import { formatNumber, getAlertMessageFunction } from '../../../common/Util';

import previousSmileIcon from '../../../asset/img/previous_smile_icon.svg';
import aiInfoIcon from '../../../asset/img/ai_assistant_icon_03.svg';
import InfoBanner from '../../../components/Banner';

const PreviousEmploymentForm = ({
  htmJType,
  onSave,
  onClose,
  initialFormData = null,
  onFormChange,
  isAiParsed,
}) => {
  const { t } = useTranslation('previousEmployment');
  const { t: tCommon } = useTranslation('common');
  const [formData, setFormData] = useState(initialFormData || {});
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAiParsedDialog, setShowAiParsedDialog] = useState(false);
  const [isAdminRegistered, setIsAdminRegistered] = useState(false); // 관리자 등록 여부

  const alertMessage = getAlertMessageFunction();
  const headers = headerList(t, { htmJType });

  useEffect(() => {
    const { htmSeq, htmFlag, htm024 } = initialFormData || {};
    //전직장 일괄업로드이거나 연계사번일 경우 저장못하게 막음 (수정 모드일 때만)
    const isEditMode = !!htmSeq;
    if (isEditMode && (htmFlag !== '' || htm024 !== '')) {
      setIsAdminRegistered(true);
    } else {
      setIsAdminRegistered(false);
    }
  }, [initialFormData]);

  // 섹션별로 데이터 그룹화
  const groupedHeaders = headers.reduce((acc, header) => {
    const { title } = header;
    if (!acc[title]) {
      acc[title] = [];
    }
    acc[title].push(header);
    return acc;
  }, {});

  const handleChange = (key, value) => {
    const processedValue = processInputValue(key, value);

    setFormData(prev => ({
      ...prev,
      [key]: processedValue,
    }));

    // 폼 데이터 변경 알림
    if (onFormChange) {
      onFormChange();
    }
  };

  const handleFieldFocus = key => {
    setActiveTooltip(key);
  };

  const handleFieldBlur = e => {
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.name) {
      return;
    }
    setActiveTooltip(null);
  };

  // 유효성 검사
  const validateForm = () => {
    return isValidate(formData, htmJType, headers, t);
  };

  // 저장 버튼 클릭 핸들러
  const handleSaveClick = () => {
    if (isSubmitting) return;

    // 관리자 등록 전직장인 경우 수정 불가
    if (isAdminRegistered) {
      alertMessage('관리자에 의해 등록된 전직장입니다. 수정하실 수 없습니다.', 'error');
      return;
    }

    if (!validateForm()) {
      return;
    }

    // AI로 파싱된 데이터인 경우 확인 다이얼로그 표시
    if (isAiParsed) {
      setShowAiParsedDialog(true);
      return;
    }

    // 일반 저장 진행
    executeSave();
  };

  // 실제 저장 실행
  const executeSave = async () => {
    setIsSubmitting(true);

    try {
      if (onSave) {
        const result = await onSave(formData);
        if (result && !result.success) {
          alertMessage(tCommon('alertMessage.saveFail'), 'error');
        } else if (result && result.success) {
          alertMessage(tCommon('alertMessage.saveSuccess'), 'success');
        }
      }
    } catch (error) {
      alertMessage(tCommon('alertMessage.saveFail'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // AI 파싱 확인 다이얼로그에서 확인 클릭
  const handleAiParsedConfirm = () => {
    setShowAiParsedDialog(false);
    executeSave();
  };

  return (
    <div className="displayFlexColumn" style={{ gap: '24px', paddingBottom: '80px' }}>
      {isAdminRegistered && (
        <div
          className="displayFlex"
          style={{
            gap: '10px',
            backgroundColor: 'var(--GrayScale-100)',
            padding: '8px',
            borderRadius: '8px',
          }}>
          <img src={aiInfoIcon} width={'42px'} alt={t('formSection.adminRegistered')} />
          <Typography variant="body2Medium">
            관리자에 의해 등록된 전직장입니다. 수정이 불가능합니다.
          </Typography>
        </div>
      )}
      {isAiParsed && (
        <div
          className="displayFlex"
          style={{
            gap: '10px',
            backgroundColor: 'var(--GrayScale-100)',
            padding: '8px',
            borderRadius: '8px',
          }}>
          <img src={aiInfoIcon} width={'42px'} alt={t('formSection.aiParsed')} />
          <Typography
            variant="body2Medium"
            dangerouslySetInnerHTML={{ __html: t('formSection.aiParsed') }}
          />
        </div>
      )}
      {Object.entries(groupedHeaders).map(([sectionTitle, items]) => (
        <div className="displayFlexColumn" key={sectionTitle} style={{ gap: '16px' }}>
          {/* 필수 */}
          {sectionTitle.includes(t('formSection.required')) ? (
            <div className="displayFlex" style={{ gap: '8px' }}>
              <img src={previousSmileIcon} alt={t('formSection.required')} />
              <Typography variant="subtitleBold">{sectionTitle}</Typography>
            </div>
          ) : (
            <Typography variant="subtitleBold">{sectionTitle}</Typography>
          )}
          <div className="displayFlexColumn" style={{ gap: '16px' }}>
            {items.map(item => (
              <div key={item.key} className="displayFlexColumn" style={{ gap: '4px' }}>
                {item.isDate && item.endKey ? (
                  // 기간 필드 (시작일/종료일 한 줄에 표시)
                  <>
                    <div className="displayFlex" style={{ gap: '4px', marginBottom: '4px' }}>
                      <span className="captionMedium" style={{ color: 'var(--GrayScale-700)' }}>
                        {item.label}
                      </span>
                      {(htmJType === '1' ? item.required : item.jType2Required) && (
                        // (필수)
                        <span className="captionMedium textPrimary500">
                          {t('formSection.requiredLabel')}
                        </span>
                      )}
                    </div>
                    <div className="displayFlex" style={{ gap: '10px', alignItems: 'flex-end' }}>
                      <DatePicker
                        value={formData[item.key] || ''}
                        name={item.key}
                        onChange={e => handleChange(e.target.name, e.target.value)}
                        endPosition="left"
                        disabled={isAdminRegistered}
                      />
                      <DatePicker
                        value={formData[item.endKey] || ''}
                        name={item.endKey}
                        onChange={e => handleChange(e.target.name, e.target.value)}
                        endPosition="right"
                        disabled={isAdminRegistered}
                      />
                    </div>
                  </>
                ) : item.isDate ? (
                  <DatePicker
                    label={
                      <div className="displayFlex" style={{ gap: '4px' }}>
                        <span className="captionMedium" style={{ color: 'var(--GrayScale-700)' }}>
                          {item.label}
                        </span>
                        {(htmJType === '1' ? item.required : item.jType2Required) && (
                          // (필수)
                          <span className="captionMedium textPrimary500">
                            {t('formSection.requiredLabel')}
                          </span>
                        )}
                      </div>
                    }
                    disabled={isAdminRegistered}
                    value={formData[item.key] || ''}
                    name={item.key}
                    onChange={e => handleChange(e.target.name, e.target.value)}
                  />
                ) : item.isSelect ? (
                  <SelectBox
                    label={
                      <div className="displayFlex" style={{ gap: '4px' }}>
                        <span className="captionMedium" style={{ color: 'var(--GrayScale-700)' }}>
                          {item.label}
                        </span>
                        {(htmJType === '1' ? item.required : item.jType2Required) && (
                          // (필수)
                          <span className="captionMedium textPrimary500">
                            {t('formSection.requiredLabel')}
                          </span>
                        )}
                      </div>
                    }
                    value={formData[item.key] || ''}
                    onChange={e => handleChange(item.key, e.target.value)}
                    onFocus={() => handleFieldFocus(item.key)}
                    onBlur={handleFieldBlur}
                    disabled={isAdminRegistered}
                    name={item.key}>
                    {item.options.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </SelectBox>
                ) : (
                  <TextField
                    variant="outline"
                    size="small"
                    label={
                      <div className="displayFlex" style={{ gap: '4px' }}>
                        <span className="captionMedium" style={{ color: 'var(--GrayScale-700)' }}>
                          {item.label}
                        </span>
                        {(htmJType === '1' ? item.required : item.jType2Required) && (
                          // (필수)
                          <span className="captionMedium textPrimary500">
                            {t('formSection.requiredLabel')}
                          </span>
                        )}
                      </div>
                    }
                    value={
                      formData[item.key] && item.isNumber
                        ? formatNumber(formData[item.key])
                        : formData[item.key] || ''
                    }
                    onChange={e => handleChange(item.key, e.target.value)}
                    onFocus={() => handleFieldFocus(item.key)}
                    onBlur={handleFieldBlur}
                    name={item.key}
                    labelSize="small"
                    type="text"
                    inputMode={item.key !== 'htm002' ? 'numeric' : 'text'}
                    disabled={isAdminRegistered}
                  />
                )}
                <FieldTooltip
                  description={item.description}
                  image={item.tooltipImg}
                  isOpen={activeTooltip === item.key}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* 저장 버튼 - 하단 고정 (관리자 등록 전직장이 아닌 경우에만 표시) */}
      {!isAdminRegistered && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px',
            zIndex: 100,
          }}>
          <Button
            color="primary"
            onClick={handleSaveClick}
            value={t('formSection.saveButton')}
            disabled={isSubmitting}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* AI 파싱 확인 다이얼로그 */}
      <AiParsedDialog
        isOpen={showAiParsedDialog}
        onClose={() => setShowAiParsedDialog(false)}
        onConfirm={handleAiParsedConfirm}
      />
    </div>
  );
};

export default PreviousEmploymentForm;
